/**
 * Toast Component
 * Notification toast component for displaying feedback messages.
 */

import { useEffect } from 'react';
import type { ToastConfig } from '@types/common.types';
import { useUIStore } from '@store/uiStore';
import styles from './Toast.module.css';

/**
 * Toast item component props.
 */
interface ToastItemProps {
  toast: ToastConfig;
  onClose: (id: string) => void;
}

/**
 * Individual toast notification item.
 */
const ToastItem = ({ toast, onClose }: ToastItemProps) => {
  const duration = toast.duration || 5000;

  useEffect(() => {
    if (duration > 0 && toast.id) {
      const timer = setTimeout(() => {
        onClose(toast.id!);
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, toast.id, onClose]);

  const toastClasses = [styles.toast, styles[toast.type]].join(' ');

  return (
    <div className={toastClasses} role="alert">
      <div className={styles.content}>
        {toast.title && <strong className={styles.title}>{toast.title}</strong>}
        <p className={styles.message}>{toast.message}</p>
      </div>
      <button
        className={styles.closeButton}
        onClick={() => toast.id && onClose(toast.id)}
        aria-label="Close notification"
      >
        {/* TODO: Add close icon */}
        &times;
      </button>
    </div>
  );
};

/**
 * Toast container component.
 * Renders all active toast notifications.
 */
export const ToastContainer = () => {
  const toasts = useUIStore((state) => state.toasts);
  const removeToast = useUIStore((state) => state.removeToast);

  if (toasts.length === 0) return null;

  return (
    <div className={styles.container} aria-live="polite">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onClose={removeToast} />
      ))}
    </div>
  );
};

